/**
 * The two screens a **host** needs — `docs/identity-and-sign-in.md` §5.2, §5.4.
 *
 * Every client is one label in front of the front door, which means two things
 * exist now that did not before: a subdomain that names nobody, and a front
 * door that somebody arrives at when they meant their own company.
 *
 * Both are answered here, and both answer the same way: by asking for the
 * company's address rather than guessing. The one thing neither may ever do is
 * send somebody on to *a* company — an unknown address resolving to whichever
 * client happened to be first would be the exact failure a subdomain per client
 * is for.
 */
import { useState } from 'react'
import { SLUG_PATTERN, isReservedSlug } from '@rowted/shared'
import { Button } from '../components/primitives.js'
import { RowtedMark } from '../frame/logo.js'
import { useHost } from '../lib/use-host.js'
import { cn } from '../lib/cn.js'

/** One card, centred, no nav — the same shell the sign-in page uses. */
function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-app items-center justify-center bg-bg p-4">
      <div className={cn('flex w-full max-w-[440px] flex-col gap-4 rounded-dialog',
        'border border-line bg-surface p-7 shadow-popover')}
      >
        <span className="flex items-center gap-2">
          <RowtedMark size={30} />
          <b className="text-[17px] tracking-[-0.02em] text-text">rowted</b>
        </span>
        {children}
      </div>
    </div>
  )
}

/**
 * The box that takes a company's short name and goes there.
 *
 * It validates the **shape** in the page and nothing more. It deliberately does
 * not ask the server whether the name exists: an endpoint that answered that
 * from the front door, with no account and no rate limit that matters, is a
 * list of Rowted's customers for anybody who wants one. So the browser simply
 * goes to the address, and the address itself answers — with the company's own
 * sign-in page, or with `NoCompanyHere` below.
 */
function AddressBox({ label, hint, action }: {
  label: string; hint: string; action: string
}) {
  const host = useHost()
  const root = host.state === 'ready'
    ? host.host.rootHost
    : host.state === 'nobody' && host.rootHost ? host.rootHost : null
  const [slug, setSlug] = useState('')

  const cleaned = slug.trim().toLowerCase()
  const ok = SLUG_PATTERN.test(cleaned) && !isReservedSlug(cleaned)

  const go = (event: React.FormEvent) => {
    event.preventDefault()
    if (!ok || !root) return
    /*
     * A whole page load, to a different origin. `navigate()` cannot help here —
     * the point of the journey is to land on a host whose cookies are its own.
     */
    const scheme = root.startsWith('localhost') || root.startsWith('127.0.0.1')
      ? 'http'
      : 'https'
    window.location.assign(`${scheme}://${cleaned}.${root}/`)
  }

  return (
    <form onSubmit={go} className="flex flex-col gap-3">
      <label className="flex flex-col gap-1.5">
        <span className="text-fieldlabel text-text-muted">{label}</span>
        <span className="flex items-center gap-1.5">
          <input
            value={slug}
            onChange={(event) => setSlug(event.target.value)}
            placeholder="keystone"
            autoFocus
            autoCapitalize="none"
            spellCheck={false}
            aria-label={label}
            className={cn('h-9 min-w-0 flex-1 rounded-control border border-line bg-surface',
              'px-[11px] text-body font-semibold text-text')}
          />
          <span className="shrink-0 text-cell text-text-muted">.{root ?? '…'}</span>
        </span>
        <span className="text-[11px] leading-[1.45] text-text-muted">{hint}</span>
      </label>
      <Button type="submit" variant="primary" disabled={!ok || !root}>{action}</Button>
    </form>
  )
}

/**
 * A subdomain that names no company — §5.2.
 *
 * It says so plainly and offers the box, rather than redirecting anywhere. A
 * redirect to the front door would look like the address worked; a redirect to
 * another client would be a security bug wearing a convenience.
 */
export function NoCompanyHere() {
  return (
    <Card>
      <div className="flex flex-col gap-1">
        <b className="text-[20px] tracking-[-0.01em] text-text">
          There’s no company at this address
        </b>
        <span className="text-cell leading-[1.45] text-text-muted">
          <b className="text-text">{window.location.host}</b> isn’t a Rowted company.
          Check the spelling — or type the short name your company uses.
        </span>
      </div>
      <AddressBox
        label="Your company’s address"
        hint="The short name in the address your colleagues use."
        action="Go there"
      />
    </Card>
  )
}

/**
 * `/crew` on the front door — §5.4.
 *
 * A shared iPad is registered to one company, and the PIN screen shows that
 * company's mark and name. On `app.rowted.com` there is no company, so there
 * is nothing to show and no PIN to check: one field, and the tab it opens is
 * the one somebody can leave on the truck.
 */
export function CrewDoor() {
  return (
    <Card>
      <div className="flex flex-col gap-1">
        <b className="text-[20px] tracking-[-0.01em] text-text">Which company?</b>
        <span className="text-cell leading-[1.45] text-text-muted">
          The crew app lives at your company’s own address, so the iPad is only
          ever signed in to one company.
        </span>
      </div>
      <AddressBox
        label="Your company’s address"
        hint="Ask the office if you’re not sure. Bookmark the page it opens."
        action="Open the crew app"
      />
    </Card>
  )
}
